import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import {
  fetchBills,
  previewBills,
  generateBills,
  updateBill,
  deleteBill,
  clearBillPreviews,
} from '../../store/slices/financeSlice'
import type { PaymentStatus, BillUpdateInput, MonthlyBill, BillAnnouncementUpdateInput, BillPreview, PaymentMethod } from '@/types/finance'
import { PaymentStatusBadge, FinanceAuditLog } from '../../components/finance'
import { useToast } from '../../components/ToastContainer'
import { useFinancePageSize, FINANCE_PAGE_SIZE_OPTIONS } from '../../hooks/useFinancePageSize'
import usePermissions, { Permission } from '../../hooks/usePermissions'
import {
  getBills as apiFetchBills,
  batchUpdateAnnouncements,
  exportBillsXlsx,
  exportAchXlsx,
  exportUnpaidSummary,
  exportUnpaidMatrix,
  recomputeBills,
  getUnmatchedRecords,
  resolveUnmatchedRecord,
} from '../../services/api/finance'
import {
  DocumentTextIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  TableCellsIcon,
  CheckBadgeIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

const paymentStatusOptions: { value: PaymentStatus; label: string }[] = [
  { value: 'UNPAID', label: '未繳' },
  { value: 'PAID', label: '已繳' },
  { value: 'ACH_SUCCESS', label: 'ACH成功' },
  { value: 'ACH_FAILED', label: 'ACH失敗' },
  { value: 'MANUAL_VERIFIED', label: '人工核銷' },
  { value: 'PARTIAL', label: '部分繳納' },
  { value: 'OVERPAID', label: '溢繳' },
  { value: 'PENDING_REVIEW', label: '待審核' },
]

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  PAPER: '紙本帳單',
  EMAIL: 'Email帳單',
  ACH: '自動扣款',
  FAX: '傳真帳單',
  POSTAL_MAIL: '郵寄帳單',
}

const PARKING_TYPE_LABELS: Record<string, string> = {
  CAR: '汽車',
  MOTOR: '機車',
  BIKE: '自行車',
  DISABLED: '殘障',
}

const BillManagement = () => {
  const { projectKey } = useParams<{ projectKey: string }>()
  const dispatch = useAppDispatch()
  const { showSuccess, showError } = useToast()
  const { currentProject } = useAppSelector(state => state.project)
  const {
    bills,
    billsPagination,
    billsSummary,
    billsLoading,
    billsError,
    billPreviews,
    billPreviewsLoading,
  } = useAppSelector(state => state.finance)

  const [billingPeriod, setBillingPeriod] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  })
  const [periodMonths, setPeriodMonths] = useState(0)
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | ''>('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useFinancePageSize()
  const { hasPermission } = usePermissions(currentProject?.id)
  const canView = hasPermission(Permission.VIEW_FINANCE_BILLS)
  const canManage = hasPermission(Permission.MANAGE_FINANCE_BILLS)
  const canDelete = hasPermission(Permission.DELETE_FINANCE_BILLS)
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  // 預覽帳單 hover 單位/繳費人/停車費時顯示的詳細資訊（fixed 定位，避免被表格捲動容器裁切）
  const [previewTip, setPreviewTip] = useState<{
    kind: 'unit' | 'payer' | 'parking'
    rect: { left: number; right: number; top: number; bottom: number }
    preview: BillPreview
  } | null>(null)
  const showPreviewTip = (kind: 'unit' | 'payer' | 'parking', preview: BillPreview) => (e: React.MouseEvent<HTMLElement>) => {
    const { left, right, top, bottom } = e.currentTarget.getBoundingClientRect()
    setPreviewTip({ kind, rect: { left, right, top, bottom }, preview })
  }
  const hidePreviewTip = () => setPreviewTip(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editReadOnly, setEditReadOnly] = useState(false)
  // 人工核銷（比對銀行存摺明細）相關狀態
  const [reconcileBill, setReconcileBill] = useState<any>(null)
  const [reconcileRange, setReconcileRange] = useState<string>('2m') // 預設前後兩個月
  const [reconcileCandidates, setReconcileCandidates] = useState<any[]>([])
  const [reconcileLoading, setReconcileLoading] = useState(false)
  const [reconcileSelectedId, setReconcileSelectedId] = useState<string>('')
  const [reconcileNote, setReconcileNote] = useState('')
  const [reconcileSubmitting, setReconcileSubmitting] = useState(false)
  const [editingBill, setEditingBill] = useState<any>(null)
  const [editFormData, setEditFormData] = useState<BillUpdateInput>({})
  const [genBillingPeriod, setGenBillingPeriod] = useState(billingPeriod)
  const [generating, setGenerating] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [selectedBills, setSelectedBills] = useState<string[]>([])
  const [showBankRecordModal, setShowBankRecordModal] = useState(false)
  const [selectedBillForRecord, setSelectedBillForRecord] = useState<any>(null)

  // Export states
  const [showExportModal, setShowExportModal] = useState(false)
  const [showAchExportModal, setShowAchExportModal] = useState(false)
  const [exportBillingPeriod, setExportBillingPeriod] = useState(billingPeriod)
  const [achBillingPeriod, setAchBillingPeriod] = useState(billingPeriod)

  // 報表匯出區
  const [reportBuilding, setReportBuilding] = useState('')
  const [reportFromPeriod, setReportFromPeriod] = useState('')
  const [reportToPeriod, setReportToPeriod] = useState('')
  const [exportingUnpaidSummary, setExportingUnpaidSummary] = useState(false)

  // 未繳交叉表
  const [matrixFromPeriod, setMatrixFromPeriod] = useState('')
  const [matrixToPeriod, setMatrixToPeriod] = useState('')
  const [matrixAllPeriods, setMatrixAllPeriods] = useState(false)
  const [exportingUnpaidMatrix, setExportingUnpaidMatrix] = useState(false)

  // 重算當期帳單
  const [recomputing, setRecomputing] = useState(false)
  const [showRecomputeResult, setShowRecomputeResult] = useState<null | {
    billingPeriod: string
    totalBills: number
    updated: number
    unchanged: number
    totalDiff: number
    details: { unitCode: string; before: number; after: number; diff: number }[]
  }>(null)
  const [exportBillsData, setExportBillsData] = useState<MonthlyBill[]>([])
  const [achBillsData, setAchBillsData] = useState<MonthlyBill[]>([])
  const [exportLoading, setExportLoading] = useState(false)
  const [exportSaving, setExportSaving] = useState(false)

  useEffect(() => {
    if (currentProject?.id) {
      dispatch(fetchBills({
        projectId: currentProject.id,
        params: {
          page,
          limit: pageSize,
          billingPeriod: billingPeriod || undefined,
          periodMonths: periodMonths || undefined,
          paymentStatus: statusFilter || undefined,
          search: search || undefined,
        },
      }))
    }
  }, [dispatch, currentProject?.id, page, pageSize, billingPeriod, periodMonths, statusFilter, search])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const parseBankRecord = (bill: any) => {
    if (!bill.bankRecord) return null
    try {
      return typeof bill.bankRecord === 'string' ? JSON.parse(bill.bankRecord) : bill.bankRecord
    } catch { return null }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const isPaidStatus = (status: string) =>
    ['PAID', 'ACH_SUCCESS', 'MANUAL_VERIFIED', 'OVERPAID', 'PARTIAL'].includes(status)

  const openBankRecordModal = (bill: any) => {
    setSelectedBillForRecord(bill)
    setShowBankRecordModal(true)
  }

  const openGenerateModal = () => {
    setGenBillingPeriod(billingPeriod)
    dispatch(clearBillPreviews())
    setShowGenerateModal(true)
  }

  const handlePreview = async () => {
    if (!currentProject?.id || !genBillingPeriod) return

    try {
      await dispatch(previewBills({
        projectId: currentProject.id,
        data: { billingPeriod: genBillingPeriod },
      })).unwrap()
    } catch (error: any) {
      showError('預覽失敗', error.message || '請稍後再試')
    }
  }

  const handleGenerate = async () => {
    if (!currentProject?.id || !genBillingPeriod) return

    setGenerating(true)
    try {
      await dispatch(generateBills({
        projectId: currentProject.id,
        data: { billingPeriod: genBillingPeriod },
      })).unwrap()
      showSuccess('帳單產生成功')
      setShowGenerateModal(false)
      dispatch(clearBillPreviews())
    } catch (error: any) {
      showError('產生失敗', error.message || '請稍後再試')
    } finally {
      setGenerating(false)
    }
  }

  const openEditModal = (bill: any, readOnly = false) => {
    setEditingBill(bill)
    setEditReadOnly(readOnly)
    setEditFormData({
      paymentStatus: bill.paymentStatus,
      paidAmount: bill.paidAmount || undefined,
      note: bill.note || '',
      baseFee: Number(bill.baseFee) || 0,
      carParkingFee: Number(bill.carParkingFee) || 0,
      motorParkingFee: Number(bill.motorParkingFee) || 0,
      bikeParkingFee: Number(bill.bikeParkingFee) || 0,
      extra1: Number(bill.extra1) || 0,
      extra2: Number(bill.extra2) || 0,
      announcement1: bill.announcement1 || '',
      announcement2: bill.announcement2 || '',
      announcement3: bill.announcement3 || '',
      announcement4: bill.announcement4 || '',
      announcement5: bill.announcement5 || '',
      announcement6: bill.announcement6 || '',
    })
    setShowEditModal(true)
  }

  const closeEditModal = () => {
    setShowEditModal(false)
    setEditReadOnly(false)
    setEditingBill(null)
    setEditFormData({})
  }

  // ====== 人工核銷 ======
  const reconcileRangeOptions = [
    { value: '1w', label: '前後一週' },
    { value: '2w', label: '前後兩週' },
    { value: '1m', label: '前後一個月' },
    { value: '2m', label: '前後兩個月' },
    { value: '3m', label: '前後三個月' },
    { value: '6m', label: '前後半年' },
    { value: 'all', label: '全部時間' },
  ]

  const computeDateRange = (billingPeriod: string, range: string): { startDate?: string; endDate?: string } => {
    if (range === 'all' || !billingPeriod || !/^\d{4}-\d{2}$/.test(billingPeriod)) return {}
    const [y, m] = billingPeriod.split('-').map(Number)
    // 以帳單期別月中為中心點
    const center = new Date(y, m - 1, 15)
    const start = new Date(center)
    const end = new Date(center)
    if (range.endsWith('w')) {
      const n = parseInt(range, 10)
      start.setDate(start.getDate() - n * 7)
      end.setDate(end.getDate() + n * 7)
    } else if (range.endsWith('m')) {
      const n = parseInt(range, 10)
      start.setMonth(start.getMonth() - n)
      end.setMonth(end.getMonth() + n)
    }
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    return { startDate: fmt(start), endDate: fmt(end) }
  }

  const loadReconcileCandidates = async (bill: any, range: string) => {
    if (!currentProject?.id) return
    setReconcileLoading(true)
    try {
      const { startDate, endDate } = computeDateRange(bill.billingPeriod, range)
      const res = await getUnmatchedRecords(currentProject.id, {
        status: 'PENDING',
        fileType: 'PASSBOOK',
        startDate,
        endDate,
        limit: 500,
        page: 1,
      })
      if (res.success && res.data) {
        // 過濾掉有虛擬帳號的記錄（只留沒有虛擬帳號的）
        const filtered = res.data.records.filter(r => {
          try {
            const data = JSON.parse(r.rawData)
            return !data?.virtualAccount
          } catch {
            return true
          }
        })
        // 依金額與帳單應繳差距排序（差距小的排前面），再依交易日期升冪
        const billAmount = Number(bill.totalAmount || 0)
        const sorted = [...filtered].sort((a, b) => {
          const da = Math.abs(Number(a.amount) - billAmount)
          const db = Math.abs(Number(b.amount) - billAmount)
          if (da !== db) return da - db
          return new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()
        })
        setReconcileCandidates(sorted)
      }
    } catch (error: any) {
      showError('載入失敗', error.message || '無法載入銀行存摺紀錄')
    } finally {
      setReconcileLoading(false)
    }
  }

  const openReconcileModal = async (bill: any) => {
    setReconcileBill(bill)
    setReconcileRange('2m')
    setReconcileSelectedId('')
    setReconcileNote(`人工核銷：${bill.unit?.unitCode || ''} ${bill.billingPeriod}`)
    setReconcileCandidates([])
    await loadReconcileCandidates(bill, '2m')
  }

  const closeReconcileModal = () => {
    setReconcileBill(null)
    setReconcileCandidates([])
    setReconcileSelectedId('')
    setReconcileNote('')
  }

  const handleReconcileRangeChange = async (range: string) => {
    setReconcileRange(range)
    setReconcileSelectedId('')
    if (reconcileBill) await loadReconcileCandidates(reconcileBill, range)
  }

  const handleReconcileSubmit = async () => {
    if (!currentProject?.id || !reconcileBill || !reconcileSelectedId) return
    setReconcileSubmitting(true)
    try {
      const note = reconcileNote.trim() || `人工核銷：${reconcileBill.unit?.unitCode || ''} ${reconcileBill.billingPeriod}`
      await resolveUnmatchedRecord(currentProject.id, reconcileSelectedId, {
        action: 'match',
        matchedBillId: reconcileBill.id,
        resolveNote: note,
      })
      showSuccess('人工核銷完成')
      closeReconcileModal()
      // 重新整理帳單列表
      dispatch(fetchBills({
        projectId: currentProject.id,
        params: {
          page,
          limit: pageSize,
          billingPeriod: billingPeriod || undefined,
          periodMonths: periodMonths || undefined,
          paymentStatus: statusFilter || undefined,
          search: search || undefined,
        },
      }))
    } catch (error: any) {
      showError('核銷失敗', error.message || '請稍後再試')
    } finally {
      setReconcileSubmitting(false)
    }
  }

  const handleUpdateBill = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentProject?.id || !editingBill) return

    setSubmitting(true)
    try {
      await dispatch(updateBill({
        projectId: currentProject.id,
        billId: editingBill.id,
        data: editFormData,
      })).unwrap()
      showSuccess('帳單更新成功')
      closeEditModal()
    } catch (error: any) {
      showError('更新失敗', error.message || '請稍後再試')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (bill: any) => {
    if (!currentProject?.id) return
    if (!confirm(`確定要刪除 ${bill.unit?.unitCode || ''} 的帳單嗎？`)) return

    try {
      await dispatch(deleteBill({
        projectId: currentProject.id,
        billId: bill.id,
      })).unwrap()
      showSuccess('帳單已刪除')
    } catch (error: any) {
      showError('刪除失敗', error.message || '請稍後再試')
    }
  }

  const toggleSelectBill = (billId: string) => {
    setSelectedBills(prev =>
      prev.includes(billId)
        ? prev.filter(id => id !== billId)
        : [...prev, billId]
    )
  }

  const toggleSelectAll = () => {
    if (selectedBills.length === bills.length) {
      setSelectedBills([])
    } else {
      setSelectedBills(bills.map(b => b.id))
    }
  }

  // 為空的公告欄帶入預設值
  const fillDefaultAnnouncements = (bills: MonthlyBill[], period: string): MonthlyBill[] => {
    const monthNum = parseInt(period.split('-')[1], 10)
    const defaultAnn1 = '1.此繳款帳號每月都不同，請勿將此繳款帳號設定為約定帳號'
    const defaultAnn2 = `2.${monthNum}月份管理費請於${monthNum}月15日前繳納。`
    return bills.map(b => ({
      ...b,
      announcement1: b.announcement1 || defaultAnn1,
      announcement2: b.announcement2 || defaultAnn2,
    }))
  }

  // Export handlers
  const openExportModal = () => {
    setExportBillingPeriod(billingPeriod)
    setExportBillsData([])
    setShowExportModal(true)
  }

  const loadExportBills = async (period: string) => {
    if (!currentProject?.id || !period) return
    setExportLoading(true)
    try {
      const res = await apiFetchBills(currentProject.id, { billingPeriod: period, limit: 500 })
      setExportBillsData(fillDefaultAnnouncements(res.data?.bills || [], period))
    } catch (error: any) {
      showError('載入失敗', error.message || '無法載入帳單資料')
    } finally {
      setExportLoading(false)
    }
  }

  const openAchExportModal = () => {
    setAchBillingPeriod(billingPeriod)
    setAchBillsData([])
    setShowAchExportModal(true)
  }

  const loadAchBills = async (period: string) => {
    if (!currentProject?.id || !period) return
    setExportLoading(true)
    try {
      const res = await apiFetchBills(currentProject.id, { billingPeriod: period, limit: 500 })
      const achBills = (res.data?.bills || []).filter((b: MonthlyBill) => b.unit?.paymentMethod === 'ACH')
      setAchBillsData(achBills)
    } catch (error: any) {
      showError('載入失敗', error.message || '無法載入帳單資料')
    } finally {
      setExportLoading(false)
    }
  }

  const handleExportBillUpdate = (index: number, field: string, value: string | number) => {
    setExportBillsData(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
  }

  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleSaveAndExport = async () => {
    if (!currentProject?.id || !exportBillingPeriod) return
    setExportSaving(true)
    try {
      // Batch update announcements
      const billUpdates: BillAnnouncementUpdateInput[] = exportBillsData.map(b => ({
        id: b.id,
        extra1: Number(b.extra1) || 0,
        extra2: Number(b.extra2) || 0,
        announcement1: b.announcement1 || '',
        announcement2: b.announcement2 || '',
        announcement3: b.announcement3 || '',
        announcement4: b.announcement4 || '',
        announcement5: b.announcement5 || '',
        announcement6: b.announcement6 || '',
      }))
      await batchUpdateAnnouncements(currentProject.id, billUpdates)

      // 檔名比照土銀上傳慣例：民國年月，例如 115年09月管理費單(無紙本).xlsx
      const [exportYear, exportMonth] = exportBillingPeriod.split('-')
      const rocPrefix = `${Number(exportYear) - 1911}年${exportMonth}月`

      // Download paper bills
      const paperBlob = await exportBillsXlsx(currentProject.id, exportBillingPeriod, 'paper')
      downloadBlob(paperBlob, `${rocPrefix}管理費單.xlsx`)

      // Download email bills
      const emailBlob = await exportBillsXlsx(currentProject.id, exportBillingPeriod, 'email')
      downloadBlob(emailBlob, `${rocPrefix}管理費單(無紙本).xlsx`)

      showSuccess('匯出成功', '已下載紙本及無紙本管理費單')
      setShowExportModal(false)
    } catch (error: any) {
      showError('匯出失敗', error.message || '請稍後再試')
    } finally {
      setExportSaving(false)
    }
  }

  const handleAchExport = async () => {
    if (!currentProject?.id || !achBillingPeriod) return
    setExportSaving(true)
    try {
      const blob = await exportAchXlsx(currentProject.id, achBillingPeriod)
      downloadBlob(blob, `${achBillingPeriod}_ACH上傳檔.xlsx`)
      showSuccess('匯出成功', '已下載 ACH 上傳檔')
      setShowAchExportModal(false)
    } catch (error: any) {
      showError('匯出失敗', error.message || '請稍後再試')
    } finally {
      setExportSaving(false)
    }
  }

  // 重算當期帳單
  const handleRecomputeBills = async () => {
    if (!currentProject?.id || !billingPeriod) return
    if (!confirm(
      `確定要重算 ${billingPeriod} 期別的所有帳單嗎？\n\n` +
      `• 會用最新的車位/管理費規則重新計算金額\n` +
      `• 不會變動繳費狀態、銷帳資料、公告欄、其他費用\n` +
      `• 已銷帳的帳單金額也會更新（status 維持 PAID）`
    )) return
    setRecomputing(true)
    try {
      const res = await recomputeBills(currentProject.id, { billingPeriod })
      if (res.success && res.data) {
        setShowRecomputeResult(res.data)
        showSuccess('重算完成', `共 ${res.data.totalBills} 張，更新 ${res.data.updated} 張`)
        // 重新載入列表
        dispatch(fetchBills({
          projectId: currentProject.id,
          params: {
            page,
            limit: pageSize,
            billingPeriod: billingPeriod || undefined,
            periodMonths: periodMonths || undefined,
            paymentStatus: statusFilter || undefined,
            search: search || undefined,
          },
        }))
      }
    } catch (error: any) {
      showError('重算失敗', error.message || '請稍後再試')
    } finally {
      setRecomputing(false)
    }
  }

  // 未繳交叉表
  const handleExportUnpaidMatrix = async () => {
    if (!currentProject?.id) return
    if (!matrixAllPeriods && (!matrixFromPeriod || !matrixToPeriod)) {
      showError('請選擇起訖月份，或勾選「全部週期」')
      return
    }
    if (!matrixAllPeriods && matrixFromPeriod > matrixToPeriod) {
      showError('起始月份不可晚於結束月份')
      return
    }
    setExportingUnpaidMatrix(true)
    try {
      const blob = await exportUnpaidMatrix(currentProject.id, {
        fromPeriod: matrixFromPeriod || undefined,
        toPeriod: matrixToPeriod || undefined,
        allPeriods: matrixAllPeriods,
      })
      const today = new Date()
      const rocYear = today.getFullYear() - 1911
      const dateStr = `${rocYear}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`
      downloadBlob(blob, `未繳交叉表_${dateStr}.xlsx`)
      showSuccess('匯出成功', '已下載未繳交叉表')
    } catch (error: any) {
      showError('匯出失敗', error.message || error.response?.data?.error || '請稍後再試')
    } finally {
      setExportingUnpaidMatrix(false)
    }
  }

  // 未繳彙總表
  const handleExportUnpaidSummary = async () => {
    if (!currentProject?.id) return
    setExportingUnpaidSummary(true)
    try {
      const blob = await exportUnpaidSummary(currentProject.id, {
        building: reportBuilding || undefined,
        fromPeriod: reportFromPeriod || undefined,
        toPeriod: reportToPeriod || undefined,
      })
      const today = new Date()
      const rocYear = today.getFullYear() - 1911
      const dateStr = `${rocYear}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`
      downloadBlob(blob, `未繳彙總表_${reportBuilding || '全部'}_${dateStr}.xlsx`)
      showSuccess('匯出成功', '已下載住戶未繳彙總表')
    } catch (error: any) {
      showError('匯出失敗', error.message || '請稍後再試')
    } finally {
      setExportingUnpaidSummary(false)
    }
  }

  // Statistics - 使用 API 回傳的統計數據 (包含所有符合篩選條件的帳單)
  const stats = billsSummary ? {
    total: billsSummary.totalCount,
    paid: billsSummary.paidCount,
    unpaid: billsSummary.unpaidCount,
    totalAmount: billsSummary.totalAmount,
    paidAmount: billsSummary.paidAmount,
  } : {
    // 如果沒有 summary，使用當前頁面資料計算 (相容舊版)
    total: bills.length,
    paid: bills.filter(b => ['PAID', 'ACH_SUCCESS', 'MANUAL_VERIFIED'].includes(b.paymentStatus)).length,
    unpaid: bills.filter(b => ['UNPAID', 'ACH_FAILED'].includes(b.paymentStatus)).length,
    totalAmount: bills.reduce((sum, b) => sum + Number(b.totalAmount || 0), 0),
    paidAmount: bills.reduce((sum, b) => sum + Number(b.paidAmount || 0), 0),
  }

  if (!canView) {
    return (
      <div className="p-6 max-w-2xl mx-auto mt-12 text-center bg-white rounded-lg shadow">
        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">無權限存取</h2>
        <p className="mt-2 text-sm text-gray-600">您沒有檢視帳單管理的權限，請聯絡專案管理員。</p>
        <Link to={`/project/${projectKey}/finance`} className="inline-block mt-4 text-blue-600 hover:underline">回財務管理首頁</Link>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/project/${projectKey}/finance`}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div className="flex items-center space-x-3">
            <DocumentTextIcon className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">帳單管理</h1>
              <p className="text-sm text-gray-500">產生與管理月費帳單</p>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {canManage && (
            <button
              onClick={openExportModal}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              匯出帳單檔
            </button>
          )}
          {canManage && (
            <button
              onClick={openAchExportModal}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <TableCellsIcon className="w-4 h-4 mr-2" />
              匯出ACH檔
            </button>
          )}
          {canManage && (
            <button
              onClick={handleRecomputeBills}
              disabled={recomputing || !billingPeriod}
              title="用最新的車位/管理費規則重算當期帳單金額，不會變動繳費狀態"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              {recomputing ? '重算中...' : '重算當期帳單'}
            </button>
          )}
          {canManage && (
            <button
              onClick={openGenerateModal}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              產生帳單
            </button>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">本期應收</div>
          <div className="text-2xl font-semibold text-gray-900">{formatCurrency(stats.totalAmount)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">已收金額</div>
          <div className="text-2xl font-semibold text-green-600">{formatCurrency(stats.paidAmount)}</div>
        </div>
      </div>

      {/* 報表匯出區 */}
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-3 border-l-4 border-blue-500 pl-2">報表匯出</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 住戶未繳彙總表 */}
          <div className="border border-gray-200 rounded-md p-3">
            <h3 className="text-sm font-medium text-gray-900 mb-2">住戶未繳彙總表</h3>
            <p className="text-xs text-gray-500 mb-3">列出所有未繳帳單，依戶聚合並顯示關聯車位</p>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div>
                <label className="block text-xs text-gray-600 mb-0.5">棟別</label>
                <input
                  type="text"
                  value={reportBuilding}
                  onChange={(e) => setReportBuilding(e.target.value)}
                  placeholder="如 A 棟"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-0.5">期間起</label>
                <input
                  type="month"
                  value={reportFromPeriod}
                  onChange={(e) => setReportFromPeriod(e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-0.5">期間迄</label>
                <input
                  type="month"
                  value={reportToPeriod}
                  onChange={(e) => setReportToPeriod(e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                />
              </div>
            </div>
            <button
              onClick={handleExportUnpaidSummary}
              disabled={exportingUnpaidSummary}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
              {exportingUnpaidSummary ? '匯出中...' : '匯出 xlsx'}
            </button>
          </div>
          {/* 未繳交叉表 */}
          <div className="border border-gray-200 rounded-md p-3">
            <h3 className="text-sm font-medium text-gray-900 mb-2">未繳交叉表</h3>
            <p className="text-xs text-gray-500 mb-3">每戶橫列、月份直欄的 pivot 表，標出每期未繳金額</p>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="block text-xs text-gray-600 mb-0.5">期間起</label>
                <input
                  type="month"
                  value={matrixFromPeriod}
                  onChange={(e) => setMatrixFromPeriod(e.target.value)}
                  disabled={matrixAllPeriods}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-0.5">期間迄</label>
                <input
                  type="month"
                  value={matrixToPeriod}
                  onChange={(e) => setMatrixToPeriod(e.target.value)}
                  disabled={matrixAllPeriods}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded disabled:bg-gray-100"
                />
              </div>
            </div>
            <label className="flex items-center gap-1 text-xs text-gray-700 mb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={matrixAllPeriods}
                onChange={(e) => setMatrixAllPeriods(e.target.checked)}
              />
              使用全部週期（自動隱藏無未繳的月份）
            </label>
            <button
              onClick={handleExportUnpaidMatrix}
              disabled={exportingUnpaidMatrix}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
              {exportingUnpaidMatrix ? '匯出中...' : '匯出 xlsx'}
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">帳單期間</label>
            <input
              type="month"
              value={billingPeriod}
              onChange={(e) => { setBillingPeriod(e.target.value); setPage(1) }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">收費月數</label>
            <select
              value={periodMonths}
              onChange={(e) => { setPeriodMonths(Number(e.target.value)); setPage(1) }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={0}>全部繳費期別</option>
              <option value={1}>1 個月（月繳）</option>
              <option value={3}>3 個月（季繳）</option>
              <option value={6}>6 個月（半年繳）</option>
              <option value={12}>12 個月（年繳）</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">繳費狀態</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as PaymentStatus | ''); setPage(1) }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">全部狀態</option>
              {paymentStatusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <div className="text-sm text-gray-600">
              已繳 {stats.paid} / 未繳 {stats.unpaid}
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="搜尋戶號、地址..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Pagination Bar */}
      {!billsLoading && billsPagination && (
        <div className="bg-white rounded-lg shadow px-4 py-2 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="text-sm text-gray-700">
              共 {billsPagination.total} 筆
              {billsPagination.totalPages > 1 && (
                <>，第 {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, billsPagination.total)} 筆</>
              )}
            </div>
            <div className="flex items-center space-x-1">
              <label className="text-sm text-gray-500">每頁</label>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
                className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                {FINANCE_PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span className="text-sm text-gray-500">筆</span>
            </div>
          </div>
          {billsPagination.totalPages > 1 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">
                {billsPagination.page} / {billsPagination.totalPages} 頁
              </span>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(billsPagination.totalPages, p + 1))}
                disabled={page === billsPagination.totalPages}
                className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      )}

      {billsError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-700">{billsError}</p>
        </div>
      )}

      {/* Bills Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {billsLoading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : bills.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            {billingPeriod || statusFilter ? '找不到符合條件的帳單' : '尚無帳單資料'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedBills.length === bills.length && bills.length > 0}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      單位
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      期間
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      基本費
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      停車費
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      總金額
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      狀態
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 shadow-[-4px_0_4px_-2px_rgba(0,0,0,0.06)]">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bills.map((bill) => (
                    <tr key={bill.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedBills.includes(bill.id)}
                          onChange={() => toggleSelectBill(bill.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {bill.unit?.unitCode || '-'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {bill.unit?.payer?.name || '無繳費人'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          type="button"
                          onClick={() => openEditModal(bill, true)}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                          title="檢視帳單（唯讀）"
                        >
                          {bill.billingPeriod}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(Number(bill.baseFee || 0))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(Number(bill.carParkingFee || 0) + Number(bill.motorParkingFee || 0) + Number(bill.bikeParkingFee || 0))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                        {formatCurrency(Number(bill.totalAmount || 0))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-1">
                          <PaymentStatusBadge status={bill.paymentStatus} />
                          {isPaidStatus(bill.paymentStatus) && bill.bankRecord && (
                            <button
                              onClick={() => openBankRecordModal(bill)}
                              className="text-blue-500 hover:text-blue-700 p-0.5 rounded hover:bg-blue-50"
                              title="查看對帳詳情"
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white z-10 shadow-[-4px_0_4px_-2px_rgba(0,0,0,0.06)]">
                        {canManage && (
                          <button
                            onClick={() => openEditModal(bill)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                            title="編輯"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        )}
                        {canManage && !isPaidStatus(bill.paymentStatus) && (
                          <button
                            onClick={() => openReconcileModal(bill)}
                            className="text-green-600 hover:text-green-900 mr-3"
                            title="人工核銷（比對銀行存摺）"
                          >
                            <CheckBadgeIcon className="w-4 h-4" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(bill)}
                            className="text-red-600 hover:text-red-900"
                            title="刪除"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Bottom Pagination */}
            {billsPagination && (
              <div className="px-4 py-2 border-t border-gray-200 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="text-sm text-gray-700">
                    共 {billsPagination.total} 筆
                    {billsPagination.totalPages > 1 && (
                      <>，第 {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, billsPagination.total)} 筆</>
                    )}
                  </div>
                  <div className="flex items-center space-x-1">
                    <label className="text-sm text-gray-500">每頁</label>
                    <select
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
                      className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      {FINANCE_PAGE_SIZE_OPTIONS.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-500">筆</span>
                  </div>
                </div>
                {billsPagination.totalPages > 1 && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">
                      {billsPagination.page} / {billsPagination.totalPages} 頁
                    </span>
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setPage(p => Math.min(billsPagination.totalPages, p + 1))}
                      disabled={page === billsPagination.totalPages}
                      className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Generate Bills Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-lg bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">產生帳單</h3>
              <button
                onClick={() => { setShowGenerateModal(false); dispatch(clearBillPreviews()) }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Modal parameter selectors */}
            <div className="flex items-end gap-3 mb-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">帳單月份</label>
                <input
                  type="month"
                  value={genBillingPeriod}
                  onChange={(e) => setGenBillingPeriod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <button
                  onClick={handlePreview}
                  disabled={billPreviewsLoading || !genBillingPeriod}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {billPreviewsLoading ? '計算中...' : '預覽帳單'}
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              系統會依住戶的繳費頻率（月繳/季繳/年繳）自動判斷是否需要在該月產生帳單，並計算對應月數的金額。
            </p>

            {billPreviewsLoading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">正在計算帳單...</p>
              </div>
            ) : billPreviews.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                請選擇帳單月份與收費月數，然後按「預覽帳單」
              </div>
            ) : (
              <>
                <div className="max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">單位</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">繳費人</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">頻率</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">管理費</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">停車費</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">總計</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {billPreviews.map((preview, idx) => {
                        const parkingFee = Number(preview.carParkingFee || 0) + Number(preview.motorParkingFee || 0) + Number(preview.bikeParkingFee || 0) + Number(preview.disabledParkingFee || 0)
                        const parkingSpaces = preview.parkingSpaces || []
                        const total = Number(preview.totalAmount || preview.total || 0)
                        const freqLabel = preview.paymentFrequency === 'QUARTERLY' ? '季繳' : preview.paymentFrequency === 'YEARLY' ? '年繳' : '月繳'
                        const freqColor = preview.paymentFrequency === 'QUARTERLY' ? 'bg-blue-100 text-blue-700' : preview.paymentFrequency === 'YEARLY' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                        return (
                          <tr key={idx} className={preview.alreadyExists ? 'bg-yellow-50' : ''}>
                            <td className="px-4 py-2 text-sm">
                              <span
                                className="cursor-help border-b border-dotted border-gray-400"
                                onMouseEnter={showPreviewTip('unit', preview)}
                                onMouseLeave={hidePreviewTip}
                              >
                                {preview.unitCode}
                              </span>
                              {preview.alreadyExists && <span className="ml-1 text-xs text-yellow-600">(已存在)</span>}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-500">
                              {preview.payer ? (
                                <span
                                  className="cursor-help border-b border-dotted border-gray-400"
                                  onMouseEnter={showPreviewTip('payer', preview)}
                                  onMouseLeave={hidePreviewTip}
                                >
                                  {preview.payer.name}
                                </span>
                              ) : (preview.payerName || '-')}
                            </td>
                            <td className="px-4 py-2 text-sm text-center">
                              <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${freqColor}`}>
                                {freqLabel}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-right">{formatCurrency(Number(preview.baseFee || 0))}</td>
                            <td className="px-4 py-2 text-sm text-right">
                              {parkingSpaces.length > 0 ? (
                                <span
                                  className="cursor-help border-b border-dotted border-gray-400"
                                  onMouseEnter={showPreviewTip('parking', preview)}
                                  onMouseLeave={hidePreviewTip}
                                >
                                  {formatCurrency(parkingFee)}
                                </span>
                              ) : formatCurrency(parkingFee)}
                            </td>
                            <td className="px-4 py-2 text-sm font-medium text-right">{formatCurrency(total)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan={5} className="px-4 py-2 text-sm font-medium text-right">總計</td>
                        <td className="px-4 py-2 text-sm font-bold text-right">
                          {formatCurrency(billPreviews.reduce((sum, p) => sum + Number(p.totalAmount || p.total || 0), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="flex justify-between items-center mt-4 pt-4 border-t">
                  <div className="text-sm text-gray-500">
                    {genBillingPeriod}，共 {billPreviews.length} 筆帳單
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => { setShowGenerateModal(false); dispatch(clearBillPreviews()) }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleGenerate}
                      disabled={generating}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {generating ? '產生中...' : '確認產生'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 帳單操作紀錄 */}
      <FinanceAuditLog section="bills" title="帳單操作紀錄" limit={50} />

      {/* Edit Bill Modal */}
      {showEditModal && editingBill && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-lg bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editReadOnly ? '檢視帳單' : '編輯帳單'} - {editingBill.unit?.unitCode}
                <span className="ml-2 text-sm text-gray-500">{editingBill.billingPeriod}</span>
              </h3>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpdateBill} className="space-y-4">
              <fieldset disabled={editReadOnly} className="space-y-4 border-0 p-0 disabled:opacity-80">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  繳費狀態
                </label>
                <select
                  value={editFormData.paymentStatus || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, paymentStatus: e.target.value as PaymentStatus }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {paymentStatusOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  實繳金額
                </label>
                <input
                  type="number"
                  value={editFormData.paidAmount || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, paidAmount: parseInt(e.target.value) || undefined }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`應繳: ${formatCurrency(Number(editingBill.totalAmount || 0))}`}
                />
              </div>

              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">費用明細</h4>
                  <span className="text-xs text-gray-500">
                    應繳合計：<span className="font-semibold text-gray-900">
                      {formatCurrency(
                        Math.max(0,
                          Number(editFormData.baseFee || 0) +
                          Number(editFormData.carParkingFee || 0) +
                          Number(editFormData.motorParkingFee || 0) +
                          Number(editFormData.bikeParkingFee || 0) +
                          Number(editingBill.adjustmentTotal || 0) +
                          Number(editFormData.extra1 || 0) +
                          Number(editFormData.extra2 || 0)
                        )
                      )}
                    </span>
                    <span className="ml-1 text-gray-400">（含費用調整 {formatCurrency(Number(editingBill.adjustmentTotal || 0))}，含其他一/其他二，下限為 0）</span>
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">基本費</label>
                    <input
                      type="number"
                      value={editFormData.baseFee ?? 0}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, baseFee: Number(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">汽車位費</label>
                    <input
                      type="number"
                      value={editFormData.carParkingFee ?? 0}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, carParkingFee: Number(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">機車位費</label>
                    <input
                      type="number"
                      value={editFormData.motorParkingFee ?? 0}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, motorParkingFee: Number(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">自行車位費</label>
                    <input
                      type="number"
                      value={editFormData.bikeParkingFee ?? 0}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, bikeParkingFee: Number(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">其他一</label>
                  <input
                    type="number"
                    value={editFormData.extra1 || 0}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, extra1: Number(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">其他二</label>
                  <input
                    type="number"
                    value={editFormData.extra2 || 0}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, extra2: Number(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="border-t pt-3">
                <h4 className="text-sm font-medium text-gray-700 mb-2">公告欄</h4>
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <div key={n} className="mb-2">
                    <label className="block text-xs text-gray-500 mb-0.5">公告欄 {n}</label>
                    <input
                      type="text"
                      value={(editFormData as any)[`announcement${n}`] || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, [`announcement${n}`]: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  備註
                </label>
                <textarea
                  value={editFormData.note || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, note: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="輸入備註..."
                />
              </div>
              </fieldset>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  {editReadOnly ? '關閉' : '取消'}
                </button>
                {!editReadOnly && (
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {submitting ? '更新中...' : '更新'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 人工核銷 Modal */}
      {reconcileBill && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-lg bg-white max-h-[85vh] flex flex-col">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">人工核銷</h3>
                <p className="text-sm text-gray-500 mt-1">
                  對帳單 <span className="font-semibold">{reconcileBill.unit?.unitCode}</span> ({reconcileBill.billingPeriod}) · 應繳 <span className="font-semibold">{formatCurrency(Number(reconcileBill.totalAmount || 0))}</span>
                </p>
                <p className="text-xs text-gray-400 mt-0.5">選擇一筆「待處理」且沒有虛擬帳號的銀行存摺紀錄進行人工比對</p>
              </div>
              <button onClick={closeReconcileModal} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="flex items-center space-x-2 mb-3 flex-wrap gap-y-1">
              <span className="text-sm text-gray-600">日期範圍：</span>
              {reconcileRangeOptions.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleReconcileRangeChange(opt.value)}
                  className={`px-2.5 py-1 text-xs rounded-full border ${
                    reconcileRange === opt.value
                      ? 'bg-green-100 border-green-500 text-green-800'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="border border-gray-200 rounded-md flex-1 overflow-auto">
              {reconcileLoading ? (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                </div>
              ) : reconcileCandidates.length === 0 ? (
                <div className="p-6 text-center text-gray-500 text-sm">此範圍內沒有符合的銀行存摺紀錄</div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left"></th>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">交易日期</th>
                      <th className="px-3 py-2 text-right text-xs text-gray-500">金額</th>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">備註</th>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">檔案</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {reconcileCandidates.map(r => {
                      const amountDiff = Number(r.amount) - Number(reconcileBill.totalAmount || 0)
                      const amountClass = amountDiff === 0 ? 'text-green-700 font-semibold' : 'text-gray-900'
                      return (
                        <tr
                          key={r.id}
                          className={`cursor-pointer hover:bg-green-50 ${reconcileSelectedId === r.id ? 'bg-green-100' : ''}`}
                          onClick={() => setReconcileSelectedId(r.id)}
                        >
                          <td className="px-3 py-2">
                            <input
                              type="radio"
                              checked={reconcileSelectedId === r.id}
                              onChange={() => setReconcileSelectedId(r.id)}
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {new Date(r.transactionDate).toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' })}
                          </td>
                          <td className={`px-3 py-2 text-right whitespace-nowrap ${amountClass}`}>
                            {formatCurrency(Number(r.amount))}
                            {amountDiff !== 0 && (
                              <span className="ml-1 text-xs text-gray-400">
                                ({amountDiff > 0 ? '+' : ''}{formatCurrency(amountDiff)})
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-600 max-w-[240px] truncate" title={r.memo || ''}>
                            {r.memo || '-'}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500 truncate max-w-[160px]">
                            {r.upload?.fileName || '-'}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="mt-3">
              <label className="block text-xs text-gray-500 mb-1">核銷備註（選填）</label>
              <input
                type="text"
                value={reconcileNote}
                onChange={(e) => setReconcileNote(e.target.value)}
                placeholder={`人工核銷：${reconcileBill.unit?.unitCode || ''} ${reconcileBill.billingPeriod}`}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="flex justify-end space-x-3 mt-4">
              <button
                type="button"
                onClick={closeReconcileModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                取消
              </button>
              <button
                type="button"
                disabled={!reconcileSelectedId || reconcileSubmitting}
                onClick={handleReconcileSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {reconcileSubmitting ? '核銷中...' : '確認核銷'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Bills Preview Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-5 mx-auto p-5 border w-full max-w-7xl shadow-lg rounded-lg bg-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">匯出管理費單預覽</h3>
                <p className="text-sm text-gray-500">編輯公告欄後匯出（自動下載紙本+無紙本兩個檔案）</p>
              </div>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="flex items-end gap-3 mb-4">
              <div className="flex-1 max-w-xs">
                <label className="block text-sm font-medium text-gray-700 mb-1">帳單月份</label>
                <input
                  type="month"
                  value={exportBillingPeriod}
                  onChange={(e) => setExportBillingPeriod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 text-sm"
                />
              </div>
              <button
                onClick={() => loadExportBills(exportBillingPeriod)}
                disabled={exportLoading || !exportBillingPeriod}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {exportLoading ? '載入中...' : '載入帳單'}
              </button>
            </div>

            {exportLoading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">載入帳單資料中...</p>
              </div>
            ) : exportBillsData.length === 0 ? (
              <div className="p-6 text-center text-gray-500">請選擇帳單月份並按「載入帳單」</div>
            ) : (
              <>
                <div className="max-h-[60vh] overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-xs">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-2 py-2 text-left font-medium text-gray-500 whitespace-nowrap">客戶編號</th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500 whitespace-nowrap">姓名</th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500 whitespace-nowrap">地址</th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500 whitespace-nowrap">電子郵件</th>
                        <th className="px-2 py-2 text-right font-medium text-gray-500 whitespace-nowrap">管理費</th>
                        <th className="px-2 py-2 text-right font-medium text-gray-500 whitespace-nowrap">汽車位清潔費</th>
                        <th className="px-2 py-2 text-right font-medium text-gray-500 whitespace-nowrap">機車位清潔費</th>
                        <th className="px-2 py-2 text-right font-medium text-gray-500 whitespace-nowrap">自行車位清潔費</th>
                        <th className="px-2 py-2 text-right font-medium text-gray-500 whitespace-nowrap">其他一</th>
                        <th className="px-2 py-2 text-right font-medium text-gray-500 whitespace-nowrap">其他二</th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500 whitespace-nowrap">公告欄1</th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500 whitespace-nowrap">公告欄2</th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500 whitespace-nowrap">公告欄3</th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500 whitespace-nowrap">公告欄4</th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500 whitespace-nowrap">公告欄5</th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500 whitespace-nowrap">公告欄6</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {exportBillsData.map((bill, idx) => (
                        <tr key={bill.id} className="hover:bg-gray-50">
                          <td className="px-2 py-1 whitespace-nowrap font-medium">{bill.unit?.unitCode || '-'}</td>
                          <td className="px-2 py-1 whitespace-nowrap">{bill.unit?.payer?.name || '-'}</td>
                          <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-600">{bill.unit?.address || '-'}</td>
                          <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-600">{bill.unit?.payer?.email || ''}</td>
                          <td className="px-2 py-1 text-right whitespace-nowrap">{formatCurrency(Number(bill.baseFee || 0))}</td>
                          <td className="px-2 py-1 text-right whitespace-nowrap">{formatCurrency(Number(bill.carParkingFee || 0))}</td>
                          <td className="px-2 py-1 text-right whitespace-nowrap">{formatCurrency(Number(bill.motorParkingFee || 0))}</td>
                          <td className="px-2 py-1 text-right whitespace-nowrap">{formatCurrency(Number(bill.bikeParkingFee || 0))}</td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              value={Number(bill.extra1) || 0}
                              onChange={(e) => handleExportBillUpdate(idx, 'extra1', Number(e.target.value) || 0)}
                              className="w-16 px-1 py-0.5 border border-gray-300 rounded text-xs text-right"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              value={Number(bill.extra2) || 0}
                              onChange={(e) => handleExportBillUpdate(idx, 'extra2', Number(e.target.value) || 0)}
                              className="w-16 px-1 py-0.5 border border-gray-300 rounded text-xs text-right"
                            />
                          </td>
                          {[1, 2, 3, 4, 5, 6].map(n => (
                            <td key={n} className="px-2 py-1">
                              <input
                                type="text"
                                value={(bill as any)[`announcement${n}`] || ''}
                                onChange={(e) => handleExportBillUpdate(idx, `announcement${n}`, e.target.value)}
                                className="w-32 px-1 py-0.5 border border-gray-300 rounded text-xs"
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-between items-center mt-4 pt-4 border-t">
                  <div className="text-sm text-gray-500">
                    共 {exportBillsData.length} 筆帳單
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowExportModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleSaveAndExport}
                      disabled={exportSaving}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {exportSaving ? '匯出中...' : '儲存變更並匯出'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ACH Export Modal */}
      {showAchExportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-lg bg-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">匯出 ACH 上傳檔預覽</h3>
                <p className="text-sm text-gray-500">ACH 自動扣款住戶</p>
              </div>
              <button
                onClick={() => setShowAchExportModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="flex items-end gap-3 mb-4">
              <div className="flex-1 max-w-xs">
                <label className="block text-sm font-medium text-gray-700 mb-1">帳單月份</label>
                <input
                  type="month"
                  value={achBillingPeriod}
                  onChange={(e) => setAchBillingPeriod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 text-sm"
                />
              </div>
              <button
                onClick={() => loadAchBills(achBillingPeriod)}
                disabled={exportLoading || !achBillingPeriod}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {exportLoading ? '載入中...' : '載入帳單'}
              </button>
            </div>

            {exportLoading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">載入帳單資料中...</p>
              </div>
            ) : achBillsData.length === 0 ? (
              <div className="p-6 text-center text-gray-500">請選擇帳單月份並按「載入帳單」</div>
            ) : (
              <>
                <div className="max-h-[60vh] overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">戶號</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">姓名</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">銀行代號</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">帳號</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">統編</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-500">金額</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {achBillsData.map(bill => (
                        <tr key={bill.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{bill.unit?.unitCode || '-'}</td>
                          <td className="px-3 py-2">{bill.unit?.payer?.name || '-'}</td>
                          <td className="px-3 py-2 font-mono">{(bill.unit?.payer as any)?.bankCode || '-'}</td>
                          <td className="px-3 py-2 font-mono">{(bill.unit?.payer as any)?.bankAccount || '-'}</td>
                          <td className="px-3 py-2 font-mono">{(bill.unit?.payer as any)?.idNumber || '-'}</td>
                          <td className="px-3 py-2 text-right font-medium">{formatCurrency(Number(bill.totalAmount || 0))}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan={5} className="px-3 py-2 text-right font-medium">總計</td>
                        <td className="px-3 py-2 text-right font-bold">{formatCurrency(achBillsData.reduce((sum, b) => sum + Number(b.totalAmount || 0), 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="flex justify-between items-center mt-4 pt-4 border-t">
                  <div className="text-sm text-gray-500">
                    共 {achBillsData.length} 筆 ACH 帳單
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowAchExportModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleAchExport}
                      disabled={exportSaving}
                      className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50"
                    >
                      {exportSaving ? '匯出中...' : '確認匯出'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Bank Record Detail Modal */}
      {showBankRecordModal && selectedBillForRecord && (() => {
        const bankData = parseBankRecord(selectedBillForRecord)
        return (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-lg bg-white">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">已匹配記錄詳情</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedBillForRecord.unit?.unitCode} - {selectedBillForRecord.billingPeriod}
                  </p>
                </div>
                <button
                  onClick={() => setShowBankRecordModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Bill Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500 text-xs">戶號</div>
                    <div className="font-mono font-medium">{selectedBillForRecord.unit?.unitCode || '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">地址</div>
                    <div className="truncate" title={selectedBillForRecord.unit?.address}>{selectedBillForRecord.unit?.address || '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">繳款人</div>
                    <div>{selectedBillForRecord.unit?.payer?.name || '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">聯絡電話</div>
                    <div>{selectedBillForRecord.unit?.payer?.phone || '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">帳單期別</div>
                    <div>{selectedBillForRecord.billingPeriod}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">應繳金額</div>
                    <div>{formatCurrency(Number(selectedBillForRecord.totalAmount || 0))}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">實繳金額</div>
                    <div className={`font-medium ${
                      Number(selectedBillForRecord.paidAmount) === Number(selectedBillForRecord.totalAmount)
                        ? 'text-green-600'
                        : 'text-yellow-600'
                    }`}>{formatCurrency(Number(selectedBillForRecord.paidAmount || 0))}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">繳款管道</div>
                    <div>{selectedBillForRecord.paymentChannel || bankData?.paymentChannel || '-'}</div>
                  </div>
                </div>

                {/* Bank Record Details */}
                {bankData && (
                  <>
                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">銀行對帳資訊</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-gray-500 text-xs">虛擬帳號</div>
                          <div className="font-mono">{bankData.virtualAccount || '-'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs">繳款人姓名</div>
                          <div>{bankData.payerName || '-'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs">入帳日</div>
                          <div>{bankData.depositDate || '-'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs">繳款日</div>
                          <div>{bankData.paymentDate || '-'}</div>
                        </div>
                        {bankData.amount && (
                          <div>
                            <div className="text-gray-500 text-xs">銀行金額</div>
                            <div className="font-medium">{formatCurrency(Number(bankData.amount))}</div>
                          </div>
                        )}
                        {bankData.transactionDate && (
                          <div>
                            <div className="text-gray-500 text-xs">交易日期</div>
                            <div>{typeof bankData.transactionDate === 'string' && bankData.transactionDate.includes('T')
                              ? formatDate(bankData.transactionDate)
                              : bankData.transactionDate}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-gray-500 text-xs">銷帳時間</div>
                          <div>{selectedBillForRecord.reconciledAt ? formatDate(selectedBillForRecord.reconciledAt) : '-'}</div>
                        </div>
                        {selectedBillForRecord.paidAt && (
                          <div>
                            <div className="text-gray-500 text-xs">繳款時間</div>
                            <div>{formatDate(selectedBillForRecord.paidAt)}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Details and Notes */}
                    {bankData.details && bankData.details.length > 0 && bankData.details.some((d: string) => d) && (
                      <div className="border-t border-gray-100 pt-3">
                        <div className="text-gray-500 text-xs mb-1">繳款明細</div>
                        <div className="text-sm text-gray-700">
                          {bankData.details.filter((d: string) => d).join(', ')}
                        </div>
                      </div>
                    )}

                    {bankData.notes && bankData.notes.length > 0 && bankData.notes.some((n: string) => n) && (
                      <div className="pt-2">
                        <div className="text-gray-500 text-xs mb-1">備註</div>
                        <div className="text-sm text-gray-700">
                          {bankData.notes.filter((n: string) => n).join(', ')}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {!bankData && (
                  <div className="border-t border-gray-200 pt-4 text-center text-gray-500 text-sm">
                    無銀行對帳資料
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-4 mt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowBankRecordModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  關閉
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* 重算結果 Modal */}
      {showRecomputeResult && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                重算結果 - {showRecomputeResult.billingPeriod}
              </h3>
              <button
                onClick={() => setShowRecomputeResult(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-3 mb-4 text-center">
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs text-gray-500">總計</div>
                <div className="text-2xl font-semibold text-gray-900">{showRecomputeResult.totalBills}</div>
              </div>
              <div className="bg-blue-50 rounded p-3">
                <div className="text-xs text-gray-500">已更新</div>
                <div className="text-2xl font-semibold text-blue-600">{showRecomputeResult.updated}</div>
              </div>
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs text-gray-500">無變動</div>
                <div className="text-2xl font-semibold text-gray-700">{showRecomputeResult.unchanged}</div>
              </div>
              <div className={`rounded p-3 ${showRecomputeResult.totalDiff === 0 ? 'bg-gray-50' : showRecomputeResult.totalDiff > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                <div className="text-xs text-gray-500">金額差</div>
                <div className={`text-xl font-semibold ${showRecomputeResult.totalDiff === 0 ? 'text-gray-700' : showRecomputeResult.totalDiff > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {showRecomputeResult.totalDiff > 0 ? '+' : ''}{showRecomputeResult.totalDiff.toLocaleString()}
                </div>
              </div>
            </div>
            {showRecomputeResult.details.length > 0 ? (
              <div className="border border-gray-200 rounded overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 text-xs font-medium text-gray-600 border-b border-gray-200">
                  變動明細（前 {showRecomputeResult.details.length} 筆）
                </div>
                <div className="max-h-80 overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">戶號</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">原金額</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">新金額</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">差額</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {showRecomputeResult.details.map((d, i) => (
                        <tr key={i}>
                          <td className="px-3 py-1.5 font-mono">{d.unitCode}</td>
                          <td className="px-3 py-1.5 text-right">{d.before.toLocaleString()}</td>
                          <td className="px-3 py-1.5 text-right font-semibold">{d.after.toLocaleString()}</td>
                          <td className={`px-3 py-1.5 text-right font-mono ${d.diff > 0 ? 'text-red-600' : d.diff < 0 ? 'text-green-600' : 'text-gray-400'}`}>
                            {d.diff > 0 ? '+' : ''}{d.diff.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="py-6 text-center text-gray-500">所有帳單金額皆與既有相符，無變動。</div>
            )}
            <div className="flex justify-end pt-4 border-t mt-4">
              <button
                onClick={() => setShowRecomputeResult(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                關閉
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 預覽帳單：hover 詳細資訊 tooltip */}
      {showGenerateModal && previewTip && (() => {
        const { kind, rect, preview } = previewTip
        // 靠近視窗底部時改往上顯示；停車費欄位在右側，改為右對齊
        const openUp = rect.bottom > window.innerHeight - 260
        const style: React.CSSProperties = {
          left: kind === 'parking' ? rect.right : rect.left,
          top: openUp ? rect.top - 4 : rect.bottom + 4,
          transform: `${kind === 'parking' ? 'translateX(-100%)' : ''} ${openUp ? 'translateY(-100%)' : ''}`,
        }
        const row = (label: string, value: React.ReactNode) => (
          <div key={label} className="flex gap-3">
            <span className="text-gray-400 w-16 shrink-0">{label}</span>
            <span className="break-all">{value || '-'}</span>
          </div>
        )
        return (
          <div
            className="fixed z-[60] pointer-events-none bg-gray-900 text-white text-xs rounded-md shadow-lg px-3 py-2 min-w-[200px] max-w-sm space-y-0.5"
            style={style}
          >
            {kind === 'unit' && (
              <>
                <div className="font-medium mb-1 text-gray-300">單位資訊</div>
                {row('戶號', preview.unitCode)}
                {row('棟／樓', [preview.building, preview.floor].filter(Boolean).join(' 棟 '))}
                {row('地址', preview.address)}
                {row('坪數', preview.pingArea != null ? `${Number(preview.pingArea)} 坪` : null)}
                {row('帳單方式', preview.paymentMethod ? PAYMENT_METHOD_LABELS[preview.paymentMethod as PaymentMethod] || preview.paymentMethod : null)}
                {row('本期月數', preview.periodMonths ? `${preview.periodMonths} 個月` : null)}
                {row('車位', (preview.parkingSpaces || []).map(sp => sp.spaceCode).join('、') || '無')}
                {(preview.adjustments || []).length > 0 && row(
                  '調整項目',
                  (preview.adjustments || []).map(adj => (
                    <div key={adj.id}>{adj.adjustmentType?.name || '調整'} {formatCurrency(Number(adj.amount))}</div>
                  ))
                )}
              </>
            )}
            {kind === 'payer' && preview.payer && (
              <>
                <div className="font-medium mb-1 text-gray-300">繳費人資訊</div>
                {row('姓名', preview.payer.name)}
                {row('電話', preview.payer.phone)}
                {row('Email', preview.payer.email)}
                {row('LINE', preview.payer.lineId)}
                {row('扣款帳號', preview.payer.bankAccount ? [preview.payer.bankCode, preview.payer.bankAccount].filter(Boolean).join('-') : null)}
                {row('通訊地址', preview.payer.ownerMeetingAddress)}
              </>
            )}
            {kind === 'parking' && (
              <>
                <div className="font-medium mb-1 text-gray-300">車位明細</div>
                {(preview.parkingSpaces || []).map((sp) => (
                  <div key={sp.spaceCode} className="flex justify-between gap-4 whitespace-nowrap">
                    <span>
                      <span className="text-gray-400 mr-1">{PARKING_TYPE_LABELS[sp.type] || sp.type}</span>
                      {sp.spaceCode}
                      {sp.note && <span className="text-gray-400 ml-1">（{sp.note}）</span>}
                    </span>
                    <span>{formatCurrency(sp.fee)}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        )
      })()}
    </div>
  )
}

export default BillManagement
